import { useEffect, useRef, useState } from "react";
import { uploadMedia, deleteMedia, mediaUrl } from "@/villa142/api";
import { fmtVoiceDate, fmtSeconds, voiceGuidance } from "@/villa142/config";
import { AudioPlayer } from "@/villa142/player";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const PRIVACY_SEEN_KEY = "villa142_voice_privacy_seen";
const PRIVACY_COPY = "Voice notes are private and shared only with the Ōraṇi team.";
const privacyWasSeen = () => {
  try { return localStorage.getItem(PRIVACY_SEEN_KEY) === "1"; } catch { return false; }
};
const markPrivacySeen = () => {
  try { localStorage.setItem(PRIVACY_SEEN_KEY, "1"); } catch { /* storage unavailable */ }
};

const MicIcon = () => (
  <svg className="v142-mic-svg" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
    <rect x="9" y="3" width="6" height="11" rx="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <line x1="12" y1="17.5" x2="12" y2="20.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <line x1="8.5" y1="20.5" x2="15.5" y2="20.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const VoiceNotes = ({ entryKey, contributor, media, ops, maxNotes = 3, maxSeconds = 180, guidance, deniedText }) => {
  const audios = (media || []).filter((m) => m.kind === "audio");
  const [state, setState] = useState("idle"); // idle | recording | uploading | retry | denied
  const [seconds, setSeconds] = useState(0);
  const [confirm, setConfirm] = useState(null); // { type: 'delete' | 'rerecord', id }
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const blobRef = useRef(null);
  const secondsRef = useRef(0);
  const [privacySeenAtMount] = useState(privacyWasSeen);

  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const upload = async (blob, duration) => {
    setState("uploading");
    blobRef.current = { blob, duration };
    try {
      const ext = blob.type.includes("mp4") ? "m4a" : "webm";
      const item = await uploadMedia(entryKey, contributor, "audio", blob, `voice-note.${ext}`, "", "", duration);
      ops.add(entryKey, contributor, item);
      blobRef.current = null;
      setState("idle");
    } catch {
      setState("retry");
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size > 0) upload(blob, secondsRef.current);
        else setState("idle");
      };
      rec.start();
      secondsRef.current = 0;
      setSeconds(0);
      setState("recording");
      timerRef.current = setInterval(() => {
        secondsRef.current += 1;
        setSeconds(secondsRef.current);
        if (secondsRef.current >= maxSeconds) stop();
      }, 1000);
    } catch {
      setState("denied");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
  };

  const removeNote = async (id) => {
    try {
      await deleteMedia(id);
      ops.remove(entryKey, contributor, id);
    } catch {}
  };

  const runConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    await removeNote(c.id);
    if (c.type === "rerecord") start();
  };

  const warning = state === "recording" && seconds >= maxSeconds - 15;
  const busy = state === "recording" || state === "uploading" || state === "retry";
  // One-time privacy reassurance: shown at the first available recording control on this
  // browser/device, below the guidance and above the initial record action. Never repeated
  // across questions, contributors, refresh, or after a note exists. Fallback replaces it.
  const showPrivacy = !privacySeenAtMount && audios.length === 0 && state !== "denied";
  useEffect(() => { if (showPrivacy) markPrivacySeen(); }, [showPrivacy]);

  return (
    <div className="v142-recorder" data-testid={`v142-recorder-${entryKey}`}>
      {audios.length > 0 && (
        <div className="v142-notes-list">
          {audios.map((m, i) => (
            <div className="v142-note" key={m.id} data-testid={`v142-note-${entryKey}-${i + 1}`}>
              <div className="v142-note-meta">
                <span className="v142-entry-group">Voice note {i + 1}</span>
                <span className="v142-entry-meta">
                  {m.duration != null ? `${fmtSeconds(m.duration)} · ` : ""}{fmtVoiceDate(m.created_at)}
                </span>
              </div>
              <AudioPlayer
                src={mediaUrl(m.id)}
                duration={m.duration}
                label={`voice note ${i + 1}`}
                testid={`v142-audio-${entryKey}-${i + 1}`}
              />
              {confirm && confirm.id === m.id ? (
                <div className="v142-note-confirm" data-testid={`v142-note-confirm-${m.id}`}>
                  <p className="v142-recorder-note">
                    {confirm.type === "delete"
                      ? "Delete this voice note? This cannot be undone."
                      : "Replace this voice note? The current note will be removed, then a new recording begins."}
                  </p>
                  <div className="v142-recorder-actions">
                    <button type="button" className="v142-link" data-testid={`v142-note-cancel-${m.id}`} onClick={() => setConfirm(null)}>Cancel</button>
                    <button type="button" className="v142-link v142-danger-link" data-testid={`v142-note-confirm-yes-${m.id}`} onClick={runConfirm}>
                      {confirm.type === "delete" ? "Delete" : "Replace and record"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="v142-recorder-actions v142-note-actions">
                  <button type="button" className="v142-link" data-testid={`v142-rerecord-${entryKey}-${i + 1}`} onClick={() => setConfirm({ type: "rerecord", id: m.id })}>Re-record</button>
                  <button type="button" className="v142-link" data-testid={`v142-delete-audio-${entryKey}-${i + 1}`} onClick={() => setConfirm({ type: "delete", id: m.id })}>Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {state === "recording" ? (
        <div className="v142-recorder-live">
          <span className="v142-rec-dot" aria-hidden="true" />
          <span className="v142-rec-time" aria-live="polite" data-testid={`v142-rec-time-${entryKey}`}>
            <span className="v142-rec-label">Recording · </span>{fmt(seconds)} / {fmt(maxSeconds)}
          </span>
          <button type="button" className="v142-btn v142-btn-small" data-testid={`v142-stop-recording-${entryKey}`} onClick={stop}>Stop</button>
          {warning && (
            <span className="v142-recorder-note v142-rec-warning" aria-live="polite" data-testid={`v142-rec-warning-${entryKey}`}>
              15 seconds left — it will finish and save on its own.
            </span>
          )}
        </div>
      ) : state === "uploading" ? (
        <p className="v142-recorder-note" aria-live="polite">Saving your voice note…</p>
      ) : state === "retry" ? (
        <div className="v142-recorder-live">
          <span className="v142-recorder-note">Your recording is kept on this screen.</span>
          <button type="button" className="v142-btn v142-btn-small" data-testid={`v142-retry-audio-${entryKey}`} onClick={() => upload(blobRef.current.blob, blobRef.current.duration)}>Try saving again</button>
        </div>
      ) : audios.length === 0 ? (
        <>
          <p className="v142-recorder-note v142-voice-guide">{guidance || voiceGuidance(maxNotes, maxSeconds)}</p>
          {showPrivacy && (
            <p className="v142-voice-privacy" data-testid={`v142-voice-privacy-${entryKey}`}>{PRIVACY_COPY}</p>
          )}
          <button type="button" className="v142-voice-btn" data-testid={`v142-add-voice-${entryKey}`} onClick={start}>
            <span className="v142-voice-ic" aria-hidden="true"><MicIcon /></span>
            Record a voice note
          </button>
        </>
      ) : audios.length < maxNotes ? (
        <button type="button" className="v142-voice-add-more" data-testid={`v142-add-voice-${entryKey}`} onClick={start}>
          <span className="v142-voice-ic" aria-hidden="true"><MicIcon /></span>
          Add voice note {audios.length + 1} of {maxNotes}
        </button>
      ) : null}

      {state === "denied" && !busy && (
        <p className="v142-recorder-note" role="alert">{deniedText || "Microphone access was declined — you are always welcome to write instead."}</p>
      )}
    </div>
  );
};
